import { DexieSyncStore, LocalDb } from '@pos/local-db';

// Varios negocios en el mismo dispositivo: cada uno tiene SU base local (IndexedDB aparte), como
// si fueran navegadores distintos. Así no se mezcla nada y cada uno funciona sin conexión. La base
// activa se elige en localStorage; cambiar de negocio recarga la app sobre la otra base. La primera
// (la de siempre) se llama 'pos-saas', para no tener que migrar nada.

export const DEFAULT_DB = 'pos-saas';
const ACTIVE_KEY = 'pos-saas-active-db';
const LIST_KEY = 'pos-saas-businesses';

/** Negocio guardado en este dispositivo. id y name quedan en null hasta habilitarlo. */
export interface KnownBusiness {
  db: string;
  id: string | null;
  name: string | null;
  logo_path?: string | null;
}

function read<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
}

function write(key: string, value: unknown): void {
  try {
    localStorage.setItem(key, typeof value === 'string' ? value : JSON.stringify(value));
  } catch {
    // Sin almacenamiento: se sigue con la base por defecto.
  }
}

export function activeDbName(): string {
  try {
    return localStorage.getItem(ACTIVE_KEY) || DEFAULT_DB;
  } catch {
    return DEFAULT_DB;
  }
}

export const db = new LocalDb(activeDbName());
export const syncStore = new DexieSyncStore(db);

/** Negocios de este dispositivo (siempre incluye el activo). */
export function knownBusinesses(): KnownBusiness[] {
  const list = read<KnownBusiness[]>(LIST_KEY, []).filter((b) => b && typeof b.db === 'string');
  const active = activeDbName();
  return list.some((b) => b.db === active) ? list : [{ db: active, id: null, name: null }, ...list];
}

/** Anota (o actualiza) el negocio de la base activa. */
export function rememberActiveBusiness(id: string, name: string, logoPath: string | null = null): void {
  const active = activeDbName();
  const list = knownBusinesses().map((b) => (b.db === active ? { db: active, id, name, logo_path: logoPath } : b));
  write(LIST_KEY, list);
}

/** Pasa a otra base y recarga la app (todo el estado en memoria se reinicia). */
export function openBusinessDb(dbName: string): void {
  write(ACTIVE_KEY, dbName);
  window.location.assign('/');
}

/** Base nueva, vacía, para habilitar otro negocio en este dispositivo. */
export function newBusinessDbName(): string {
  const name = `${DEFAULT_DB}-${crypto.randomUUID().slice(0, 8)}`;
  write(LIST_KEY, [...knownBusinesses(), { db: name, id: null, name: null }]);
  return name;
}

/** Saca de la lista una base que nunca se llegó a habilitar (por ejemplo, «Agregar» por error). */
export function forgetEmptyBusinessDb(dbName: string): void {
  write(
    LIST_KEY,
    knownBusinesses().filter((b) => b.db !== dbName || b.id !== null),
  );
}

/** Pide al navegador que no borre los datos locales cuando le falte espacio. */
export async function requestPersistentStorage(): Promise<boolean> {
  try {
    if (navigator.storage?.persisted && (await navigator.storage.persisted())) return true;
    return (await navigator.storage?.persist?.()) ?? false;
  } catch {
    return false;
  }
}
